import {DateRange} from '@hconnect/common/types'
import moment from 'moment-timezone'
import {useEffect, useMemo, useRef} from 'react'

import {CqdbEquipmentDto, LatestSampledEquipment} from '../types'

import {useGetLatestSampledEquipment} from './api/labData'

type LatestSampleDataParams = {
  plantId: string
  equipments: CqdbEquipmentDto[] | undefined
  timezone: string
  onTimeRangeChange: (timeRange: DateRange) => void
}

type LatestSampleDataResult = {
  latestSample: LatestSampledEquipment | undefined
  isLoading: boolean
  orderedEquipments: CqdbEquipmentDto[] | undefined
}

export const sortEquipmentsByMeasurementData = (
  equipments: CqdbEquipmentDto[] | undefined
): CqdbEquipmentDto[] | undefined => {
  if (!equipments) return undefined

  const withMeasurements = equipments
    .filter((equipment) => equipment.hasMeasurementData)
    .sort((left, right) => left.limsSamplingPointCode.localeCompare(right.limsSamplingPointCode))

  const withoutMeasurements = equipments
    .filter((equipment) => !equipment.hasMeasurementData)
    .sort((left, right) => left.limsSamplingPointCode.localeCompare(right.limsSamplingPointCode))

  return [...withMeasurements, ...withoutMeasurements]
}

export const createDateRangeFromLatestSample = (
  latestSample: LatestSampledEquipment | undefined,
  timezone: string
): DateRange | undefined => {
  if (!latestSample?.startDate || !latestSample?.endDate) return undefined

  return {
    startDate: moment(latestSample.startDate).tz(timezone),
    endDate: moment(latestSample.endDate).tz(timezone)
  }
}

export const useLatestSampleData = ({
  plantId,
  equipments,
  timezone,
  onTimeRangeChange
}: LatestSampleDataParams): LatestSampleDataResult => {
  const {data: latestSample, isLoading} = useGetLatestSampledEquipment(plantId)

  const orderedEquipments = useMemo(() => sortEquipmentsByMeasurementData(equipments), [equipments])

  // Apply latest sample time range per plant/equipment
  const lastAppliedKey = useRef<string | null>(null)
  useEffect(() => {
    const key = `${plantId}`
    if (lastAppliedKey.current === key) return

    const dateRange = createDateRangeFromLatestSample(latestSample, timezone)
    if (dateRange) {
      lastAppliedKey.current = key
      onTimeRangeChange(dateRange)
    }
  }, [plantId, latestSample, timezone, onTimeRangeChange])

  return {
    latestSample,
    isLoading,
    orderedEquipments
  }
}
